import { AdminAsyncState } from '@/components/admin/admin-async-state';
import * as React from 'react';
import { AlertTriangle, Link2Off } from 'lucide-react';
import {
  SopFlowHealthIssueLabel,
  SopFlowHealthIssueMessage,
} from '@/components/sops-workflow/sop-flow-health-issue-message';
import { loadCachedSopStructuralFlowIssues } from '@/lib/admin-notification-flow-issues';
import { isSopStepLinkIssue, type SopFlowHealthIssue } from '@/lib/sops-workflow-author';

export function AdminNotificationFlowIssueList({ sopId }: { sopId: number }) {
  const [issues, setIssues] = React.useState<SopFlowHealthIssue[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [retryCount, setRetryCount] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void loadCachedSopStructuralFlowIssues(sopId)
      .then((rows) => {
        if (cancelled) return;
        setIssues(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setIssues([]);
        setLoadError('Could not load flow details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sopId, retryCount]);

  if (loading) {
    return (
      <AdminAsyncState status="loading" loadingLabel="Loading flow details…" />
    );
  }

  if (loadError) {
    return (
      <AdminAsyncState status="error" error={loadError} action={<><button
          type="button"
          className="font-medium underline underline-offset-2 hover:no-underline"
          onClick={() => setRetryCount((count) => count + 1)}
        >
          Retry
        </button></>} />
    );
  }

  if (!issues.length) return null;

  return (
    <div className="space-y-2 border-t border-destructive/20 pt-2">
      <p className="text-xs font-medium text-muted-foreground">Flow issues</p>
      {issues.map((issue) => (
        <div
          key={issue.issueKey}
          className="flex items-start gap-2 rounded-md border border-border/60 bg-background/80 p-2 text-sm"
        >
          {isSopStepLinkIssue(issue) ? (
            <Link2Off className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-text" aria-hidden />
          )}
          <span className="min-w-0 flex-1">
            <SopFlowHealthIssueLabel issue={issue} />
            <SopFlowHealthIssueMessage issue={issue} />
          </span>
        </div>
      ))}
    </div>
  );
}
